import { z } from 'zod';
import { BrowserProviderIdSchema, ExecutionCapabilitySchema } from './run-capabilities';

/**
 * Canonical run-status union. Includes 'queued' (the run is enqueued
 * but no runner has picked it up yet) which previously only existed in
 * @rynora/sdk and was missing from the flow-builder type.
 */
export const RUN_STATUSES = ['queued', 'running', 'passed', 'failed', 'cancelled'] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const RunStatusSchema = z.enum(RUN_STATUSES);

/**
 * Terminal leaf statuses for a single test_runs row.
 * Aligns with orchestrator completion, SDK wait loops, flake/quality-gate
 * filters, and flow-builder RunStatus. `queued` / `running` are non-terminal.
 *
 * QP-3 — evidence freeze applies only when the existing row is already
 * one of these; first transition into a terminal status still persists.
 */
export const TERMINAL_RUN_STATUSES = ['passed', 'failed', 'cancelled'] as const;
export type TerminalRunStatus = (typeof TERMINAL_RUN_STATUSES)[number];
export const TerminalRunStatusSchema = z.enum(TERMINAL_RUN_STATUSES);

export function isTerminalRunStatus(status: string | null | undefined): status is TerminalRunStatus {
  return status === 'passed' || status === 'failed' || status === 'cancelled';
}

/**
 * Status of a single step inside a run. 'healed' means the step
 * recovered via the self-healing engine; 'pending' means it hasn't
 * been reached yet.
 */
export const STEP_RUN_STATUSES = ['passed', 'failed', 'skipped', 'healed', 'running', 'pending'] as const;
export type StepRunStatus = (typeof STEP_RUN_STATUSES)[number];
export const StepRunStatusSchema = z.enum(STEP_RUN_STATUSES);

/**
 * Phase 5.5 · Where a run physically executed. 'local' means the
 * runner spun up a Chromium binary on the same VM. 'cloud' means
 * the runner connected over CDP to a Steel.dev session that the API
 * minted on demand. Stored on every run row so reports / billing
 * dashboards can split usage by mode without re-deriving from the
 * cloud_browser_sessions ledger.
 */
export const EXECUTION_MODES = ['local', 'cloud'] as const;
export type ExecutionMode = (typeof EXECUTION_MODES)[number];
export const ExecutionModeSchema = z.enum(EXECUTION_MODES);

/**
 * Migration 046 · Which version of the flow this run executed
 * against. Values:
 *   - 'draft'         The user pressed "Run" inside the editor while
 *                     iterating on an unpublished change.
 *   - 'published_v<N>' Scheduled / CI / API-key triggered runs that
 *                     pulled the published canvas. The published
 *                     version is suffixed so the run-history list can
 *                     show "ran v3" without joining flow_versions.
 *
 * We keep this as `z.string()` (not an enum) because the published-
 * version suffix is open-ended; the API enforces the shape on insert.
 */
// Migration 047 extends the format with `branch:<uuid>` so the
// run-history chip can render "ran on branch karthi/login-fix" for
// in-editor runs against a non-default branch. Three accepted shapes:
//   - 'draft'             — V1 default-branch draft (legacy alias)
//   - 'published_v<N>'    — scheduled / CI run against the published canvas
//   - 'branch:<uuid>'     — editor / API run against a specific branch
export const RanAgainstSchema = z
  .string()
  .regex(
    /^(draft|published_v\d+|branch:[0-9a-fA-F-]{36})$/,
    'ranAgainst must be "draft", "published_v<N>", or "branch:<uuid>"',
  );
export type RanAgainst = z.infer<typeof RanAgainstSchema>;

export const RunSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  flowName: z.string(),
  status: RunStatusSchema,
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  triggeredBy: z.string().nullable(),
  environment: z.string().nullable(),
  /** 'local' or 'cloud'. Optional + defaults to 'local' when absent
   *  so legacy rows (pre-5.5) deserialize cleanly. */
  executionMode: ExecutionModeSchema.optional().default('local'),
  /** Set only for cloud runs; foreign key into cloud_browser_sessions.id. */
  cloudSessionId: z.string().nullable().optional(),
  /**
   * Migration 046 · Which version of the flow was actually executed.
   * Optional (older rows have no value); the run-history UI renders a
   * `Draft` / `v3` chip when present.
   */
  ranAgainst: RanAgainstSchema.optional(),
  /** Migration 047 · Branch the run was triggered against (when ranAgainst='branch:<uuid>'). */
  branchId: z.string().uuid().nullable().optional(),
  /**
   * Migration 053 · Provider that physically owned the session.
   * Optional + nullable for pre-053 history rows where we couldn't
   * reconstruct the provider. New rows always have it.
   */
  provider: BrowserProviderIdSchema.nullable().optional(),
  /**
   * Migration 053 · Cross-platform capability tuple this run executed
   * against. Optional in the schema for back-compat with V1 callers
   * that only set `browser`; the API server fills in the remainder
   * before persisting (so DB rows always carry a non-empty object).
   */
  capability: ExecutionCapabilitySchema.optional(),
  /**
   * v0.46 — resolved execution target recorded at dispatch time.
   * Null for legacy rows or runs that pre-date target persistence.
   */
  executionTargetKind: z.enum(['saas', 'agent', 'cloud']).nullable().optional(),
  /** Agent runner token UUID when executionTargetKind is `agent`. */
  executionTargetId: z.string().nullable().optional(),
  /**
   * QD-1 Prerequisite #1 — CI/build identity (optional).
   * Authoritative build population requires buildId + commitSha + environment
   * on test_runs. Missing fields ⇒ non-authoritative for build-scoped QD-1.
   */
  buildId: z.string().trim().min(1).max(120).nullable().optional(),
  /** Git commit SHA (7–64). Paired with buildId for same-commit rebuild isolation. */
  commitSha: z.string().trim().min(7).max(64).nullable().optional(),
});
export type Run = z.infer<typeof RunSchema>;

/**
 * QD-1 Prerequisite #1 — wire fields for POST /api/runs (+ suite) persistence.
 * Optional for back-compat; both must be present (with environment) for
 * authoritative build population.
 */
export const RunBuildIdentityFieldsSchema = z.object({
  buildId: z.string().trim().min(1).max(120).nullable().optional(),
  commitSha: z.string().trim().min(7).max(64).nullable().optional(),
});
export type RunBuildIdentityFields = z.infer<typeof RunBuildIdentityFieldsSchema>;

/** Exact build population key — fail-closed when any part is missing. */
export const BuildPopulationKeySchema = z.object({
  projectId: z.string().uuid(),
  buildId: z.string().trim().min(1).max(120),
  commitSha: z.string().trim().min(7).max(64),
  environment: z.string().trim().min(1).max(200),
});
export type BuildPopulationKey = z.infer<typeof BuildPopulationKeySchema>;

/** Max length for QD / test_runs.environment free-text (matches BuildPopulationKey). */
export const QD_DEPLOYMENT_ENVIRONMENT_MAX = 200;

/**
 * Normalize a QD deployment-environment string (same axis as test_runs.environment).
 * Trim only — never maps editor ids/names or invents defaults.
 */
export function normalizeQdDeploymentEnvironment(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > QD_DEPLOYMENT_ENVIRONMENT_MAX) return null;
  return trimmed;
}

export const StepRunSchema = z.object({
  id: z.string(),
  runId: z.string(),
  stepIndex: z.number().int().nonnegative(),
  intent: z.string(),
  status: StepRunStatusSchema,
  durationMs: z.number().nullable(),
  errorMessage: z.string().nullable(),
  screenshotUrl: z.string().nullable(),
});
export type StepRun = z.infer<typeof StepRunSchema>;

export const ListRunsResponseSchema = z.object({
  runs: z.array(RunSchema),
  total: z.number().int().nonnegative(),
});
export type ListRunsResponse = z.infer<typeof ListRunsResponseSchema>;

export const RunDetailResponseSchema = z.object({
  run: RunSchema,
  steps: z.array(StepRunSchema),
});
export type RunDetailResponse = z.infer<typeof RunDetailResponseSchema>;

export const StartRunRequestSchema = z.object({
  flowId: z.string(),
  environment: z.string().optional(),
  parameters: z.record(z.unknown()).optional(),
  /** Phase 5.5 · Optional override of the per-environment default mode.
   *  Resolution order at the runner: explicit request > org override >
   *  env default. See packages/api/src/lib/cloud-browser-quota.ts. */
  executionMode: ExecutionModeSchema.optional(),
  /**
   * Migration 053 · Cross-platform capability the caller wants this
   * run to execute against. Optional — when omitted the API resolves
   * to the org's default capability (chromium/chrome on the host OS).
   */
  capability: ExecutionCapabilitySchema.optional(),
  /** QD-1 Prerequisite #1 — optional CI build identity (not manufactured). */
  buildId: z.string().trim().min(1).max(120).nullable().optional(),
  commitSha: z.string().trim().min(7).max(64).nullable().optional(),
});
export type StartRunRequest = z.infer<typeof StartRunRequestSchema>;

export const StartRunResponseSchema = z.object({
  runId: z.string(),
  status: RunStatusSchema,
  /** Phase 5.5 · Echoed back so the caller can show the mode chip
   *  immediately, without re-fetching the run row. */
  executionMode: ExecutionModeSchema.optional(),
  /** Phase 5.5 · Cloud session id when executionMode='cloud'. */
  cloudSessionId: z.string().nullable().optional(),
  /** Migration 053 · Provider the resolver picked for this run. */
  provider: BrowserProviderIdSchema.optional(),
  /** Migration 053 · Resolved capability the runner will execute with. */
  capability: ExecutionCapabilitySchema.optional(),
});
export type StartRunResponse = z.infer<typeof StartRunResponseSchema>;
